import { FC, memo, useState, useCallback, useMemo } from 'react';
import { Project, ProjectStatus, ProjectCreateInput } from '../../types/project';
import { ProjectCard } from '../ProjectCard/ProjectCard';
import { ProjectModal } from '../ProjectModal/ProjectModal';
import styles from './ProjectList.module.css';

interface ProjectListProps {
  projects: Project[];
  onCreateProject: (project: ProjectCreateInput) => Promise<void>;
  onUpdateProject: (id: string, project: Partial<ProjectCreateInput>) => Promise<void>;
  onDeleteProject: (id: string) => Promise<void>;
}

const statusOptions: { value: ProjectStatus | ''; label: string }[] = [
  { value: '', label: '全てのステータス' },
  { value: 'good', label: '良好' },
  { value: 'normal', label: '普通' },
  { value: 'warning', label: '炎上の可能性あり' },
  { value: 'danger', label: '炎上中' },
];

export const ProjectList: FC<ProjectListProps> = memo(({
  projects,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenCreateModal = useCallback(() => {
    setSelectedProject(null);
    setIsModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProject(null);
  }, []);

  const handleSubmit = useCallback(
    async (data: ProjectCreateInput) => {
      try {
        if (selectedProject) {
          await onUpdateProject(selectedProject.id, data);
        } else {
          await onCreateProject(data);
        }
        handleCloseModal();
      } catch (error) {
        console.error('Failed to save project:', error);
      }
    },
    [selectedProject, onCreateProject, onUpdateProject, handleCloseModal]
  );

  const handleDelete = useCallback(
    async (project: Project) => {
      if (window.confirm(`「${project.Name}」を削除してもよろしいですか？`)) {
        try {
          await onDeleteProject(project.id);
        } catch (error) {
          console.error('Failed to delete project:', error);
        }
      }
    },
    [onDeleteProject]
  );

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesSearch = project.Name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || project.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, statusFilter]);

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.controls}>
          <div className={styles.searchControls}>
            <input
              type="text"
              placeholder="案件を検索..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <select
              className={styles.statusFilter}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as ProjectStatus | '')}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            className={styles.addButton}
            onClick={handleOpenCreateModal}
          >
            新規案件追加
          </button>
        </div>
      </div>

      <div className={styles.container}>
        {filteredProjects.map(project => (
          <ProjectCard
            key={project.id}
            project={project}
            onEdit={handleOpenEditModal}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {isModalOpen && (
        <ProjectModal
          project={selectedProject || undefined}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
});

ProjectList.displayName = 'ProjectList';